import { useCallback, useState } from 'react'
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Pagination, Tooltip } from 'antd'
import useSWR from 'swr'
import Utils from 'utils'

import Spinner from 'components/atoms/Spinner'
import MemoFileIcon from 'components/icons/MemoFIleIcon'

import AnnotationMemoDetailModal from './AnnotationMemoDetailModal'
import {
  AnotationMemoFileInfo,
  AnotationMemoResponse,
  AnotationMemoType,
  MemoListProps
} from './type'

export default function AnotationMemoList({
  buildingId,
  folderType,
  childrenType
}: MemoListProps) {
  const navigate = useNavigate()
  const [memoKeyword, setMemoKeyword] = useState('')
  const [memoCurrentPage, setMemoCurrentPage] = useState(1)
  const [memoViewVisible, setMemoViewVisible] = useState<boolean>(false)
  const [selectedMemo, setSelectedMemo] =
    useState<AnotationMemoFileInfo | null>(null)
  const [searchKeyword, setSearchKeyword] = useState('')
  const handleGetAnotationMemotype = (childrenType: string) => {
    if (!childrenType) return ''

    const lastChar = childrenType.slice(-1)

    switch (lastChar) {
      case '1':
        return AnotationMemoType.NORMAL
      case '2':
        return AnotationMemoType.OCCURENCE
      case '3':
        return AnotationMemoType.RECOVERY
      default:
        return ''
    }
  }

  const { data: memoList, isValidating: isMemoLoading } =
    useSWR<AnotationMemoResponse>(
      {
        url: `/v1/building-annotation-memos?building_id=${buildingId}&memo_type=${handleGetAnotationMemotype(
          childrenType
        )}&annotation_type=${folderType}&page=${memoCurrentPage}&per_page=10${
          memoKeyword ? `&search=${encodeURIComponent(memoKeyword.trim())}` : ''
        }`
      },
      {
        onError: (err) => {
          Utils.handleErrorNavigate(err, navigate)
        }
      }
    )

  const handleMemoSearch = useCallback(() => {
    setMemoKeyword(searchKeyword.trim())
    setMemoCurrentPage(1)
  }, [searchKeyword])

  const handleViewMemoDetail = (memo: AnotationMemoFileInfo) => {
    setSelectedMemo(memo)
    setMemoViewVisible(true)
  }

  const handleCloseMemoView = () => {
    setMemoViewVisible(false)
    setSelectedMemo(null)
  }

  const handlePageChange = (page: number) => {
    setMemoCurrentPage(page)
  }

  const itemRender = (_: any, type: string, originalElement: any) => {
    if (type === 'prev') {
      return (
        <div className="icon-prev flex items-center justify-center">
          <MdKeyboardDoubleArrowLeft className="theme-text-primary" />
        </div>
      )
    }
    if (type === 'next') {
      return (
        <div className="icon-next flex items-center justify-center">
          <MdKeyboardDoubleArrowRight className="theme-text-primary" />
        </div>
      )
    }
    return originalElement
  }

  const getDisplayName = (memo: AnotationMemoFileInfo) => {
    const floorName = `${memo.floor_name || `floor_${memo.floor_id}`}`
    const pointOrder = `${memo.point_order}`
    return `${floorName}_ポイント ${pointOrder}_${
      memo.name || memo.annotation_id
    }`
  }

  return (
    <>
      <div className="rounded-lg shadow-sm bg-[##F9FBFF]">
        <div className="p-4 border-b border-gray-200 bg-white mb-5">
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                キーワード
              </span>
              <Input
                placeholder="検索キーワードを入力"
                value={searchKeyword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchKeyword(e.target.value)
                }
                onPressEnter={handleMemoSearch}
                className="w-[280px] h-[38px]"
                allowClear
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="primary"
                onClick={handleMemoSearch}
                loading={isMemoLoading}
                className="h-[40px] w-[160px] text-center"
              >
                検索
              </Button>
            </div>
          </div>
          {memoKeyword && (
            <div className="mt-2 text-sm text-gray-500">
              検索結果: {memoList?.total || 0}件
            </div>
          )}
        </div>

        <div className="theme-bg-primary text-white px-6 py-3 rounded-t-lg flex items-center gap-2">
          <MemoFileIcon />
          <span className="font-medium">申し送り一覧</span>
        </div>

        <div className="p-6 bg-white">
          {isMemoLoading && (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-2">読み込み中...</div>
              <Spinner size="large" />
            </div>
          )}

          {memoList?.data && memoList.data.length > 0 ? (
            <div className="space-y-3">
              {memoList.data.map((memo) => (
                <div
                  key={memo.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <Tooltip title={getDisplayName(memo)}>
                      <div className="text-sm text-gray-900 truncate max-w-[600px]">
                        {getDisplayName(memo)}
                      </div>
                    </Tooltip>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(memo.updated_at).toLocaleDateString('ja-JP')}
                    </div>
                  </div>
                  <Button
                    type="primary"
                    onClick={() => handleViewMemoDetail(memo)}
                    className="ml-4"
                  >
                    詳細を表示
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              メモが見つかりません
            </div>
          )}

          {memoList && Math.ceil((memoList.total || 0) / 10) > 1 && (
            <div className="p-6 border-t border-gray-200 flex justify-center">
              <Pagination
                showSizeChanger={false}
                className="my-4 custom-pagination"
                current={memoCurrentPage}
                pageSize={10}
                total={memoList.total}
                onChange={handlePageChange}
                itemRender={itemRender}
              />
            </div>
          )}
        </div>
      </div>

      <AnnotationMemoDetailModal
        visible={memoViewVisible}
        memo={selectedMemo}
        onClose={handleCloseMemoView}
      />
    </>
  )
}
